import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ShieldCheck, WifiOff, FileDown } from 'lucide-react';

import { Button } from '@/components/ui/button';

export function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-20 bg-grid-pattern [background-size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="animate-float-slow absolute -left-32 top-0 size-[520px] rounded-full bg-violet-500/35 blur-[110px] dark:bg-violet-500/25" />
        <div className="animate-float-slow absolute right-0 top-10 size-[560px] rounded-full bg-sky-400/35 blur-[110px] [animation-delay:-4s] dark:bg-sky-400/20" />
        <div className="animate-float-slow absolute left-1/3 top-40 size-[460px] rounded-full bg-fuchsia-400/30 blur-[110px] [animation-delay:-8s] dark:bg-fuchsia-500/20" />
        <div className="absolute bottom-0 right-10 size-[380px] rounded-full bg-amber-300/25 blur-[100px] dark:bg-amber-500/10" />
      </div>

      <div className="mx-auto max-w-6xl px-4 pb-16 pt-20 md:pb-24 md:pt-28">
        <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
          <div className="flex flex-col gap-6 text-center md:text-left">
            <span className="inline-flex items-center gap-2 self-center rounded-full border border-violet-200 bg-white/70 px-3 py-1 text-xs font-medium text-violet-700 backdrop-blur dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-300 md:self-start">
              <span className="size-1.5 rounded-full bg-violet-500" />
              {t('home.hero.eyebrow')}
            </span>

            <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl md:text-6xl">
              {t('home.hero.headline').split(' ').slice(0, -1).join(' ')}{' '}
              <span className="animate-gradient-shimmer bg-gradient-to-r from-violet-600 via-fuchsia-500 to-sky-500 bg-clip-text text-transparent">
                {t('home.hero.headline').split(' ').slice(-1)}
              </span>
            </h1>

            <p className="text-lg text-muted-foreground md:text-xl">{t('home.hero.subtitle')}</p>

            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center md:justify-start">
              <Button asChild size="lg">
                <Link to="/editor">
                  {t('home.hero.ctaPrimary')}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="#templates">{t('home.hero.ctaSecondary')}</a>
              </Button>
            </div>

            <ul className="mt-2 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground md:justify-start">
              <li className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
                {t('home.hero.trust1')}
              </li>
              <li className="inline-flex items-center gap-1.5">
                <WifiOff className="h-4 w-4 text-sky-500" />
                {t('home.hero.trust2')}
              </li>
              <li className="inline-flex items-center gap-1.5">
                <FileDown className="h-4 w-4 text-violet-500" />
                {t('home.hero.trust3')}
              </li>
            </ul>
          </div>

          <div className="relative mx-auto w-full max-w-md md:max-w-none">
            <HeroMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroMockup() {
  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br from-violet-400/30 via-fuchsia-300/20 to-sky-300/30 blur-2xl"
      />

      <div className="relative rotate-1 rounded-2xl border border-border bg-card p-6 shadow-2xl transition-transform duration-500 hover:rotate-0 sm:p-8">
        <div className="flex items-center gap-4 border-b border-border pb-4">
          <div className="size-14 shrink-0 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-2/3 rounded bg-foreground/80" />
            <div className="h-2.5 w-1/2 rounded bg-muted-foreground/40" />
          </div>
        </div>
        <div className="space-y-4 pt-5">
          <div>
            <div className="mb-2 h-2 w-20 rounded bg-violet-500/80" />
            <div className="space-y-1.5">
              <div className="h-2 w-full rounded bg-muted-foreground/25" />
              <div className="h-2 w-11/12 rounded bg-muted-foreground/25" />
              <div className="h-2 w-4/5 rounded bg-muted-foreground/25" />
            </div>
          </div>
          <div>
            <div className="mb-2 h-2 w-24 rounded bg-sky-500/80" />
            <div className="space-y-1.5">
              <div className="h-2 w-full rounded bg-muted-foreground/25" />
              <div className="h-2 w-10/12 rounded bg-muted-foreground/25" />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <span className="h-5 w-14 rounded-full bg-violet-100 dark:bg-violet-500/20" />
            <span className="h-5 w-14 rounded-full bg-sky-100 dark:bg-sky-500/20" />
            <span className="h-5 w-14 rounded-full bg-emerald-100 dark:bg-emerald-500/20" />
            <span className="h-5 w-14 rounded-full bg-fuchsia-100 dark:bg-fuchsia-500/20" />
            <span className="h-5 w-14 rounded-full bg-amber-100 dark:bg-amber-500/20" />
          </div>
        </div>
      </div>

      <div className="absolute -right-3 -top-3 rotate-6 rounded-xl border border-border bg-card px-3 py-2 shadow-lg sm:-right-6 sm:-top-4">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <span className="rounded bg-gradient-to-br from-violet-600 to-fuchsia-500 px-1.5 py-0.5 text-[10px] text-white">
            PDF
          </span>
          <span className="text-foreground">cv.pdf</span>
        </div>
      </div>
    </div>
  );
}
